import { makeAutoObservable } from 'mobx'

import { API_INITIAL, APIStatus } from '@flowwai-frontend/shared/api-common'
import { Collection } from '@flowwai-frontend/shared/sales-crm/sales-crm-core'

import {
   LOADING_MESSAGE_ID,
   LOADING_MESSAGE_INITIAL_CONTENT
} from '../constants'
import { Message, Thread, ThreadError } from '../types/thread'

import { RESPONSE_LOADING_CONTENT } from '../constants'
import { LoadingMessageType } from '../types'
import MessageModel from './MessageModel'

class ThreadModel {
   readonly threadId: number
   private _name: string
   private _messageCollection: Collection<number, MessageModel>

   private _receivingAPIStatus!: APIStatus

   private _threadError?: ThreadError

   private _loadingMessage: MessageModel

   private _loadingMessageStatus: LoadingMessageType | null

   private constructor(data: Thread) {
      this.threadId = data.threadId
      this._name = data.name

      this._messageCollection = new Collection<number, MessageModel>(
         "The message you are trying to access doesn't exist"
      )

      data.messages.map(msg => {
         const messageModel = MessageModel.create(msg)
         this._messageCollection.addItem(messageModel.messageId, messageModel)
      })

      this._receivingAPIStatus = API_INITIAL
      this._loadingMessageStatus = null

      this._loadingMessage = MessageModel.create({
         messageId: LOADING_MESSAGE_ID,
         content: '',
         type: 'REPLY',
         timeStamp: new Date()
      })

      makeAutoObservable(this, {}, { autoBind: true })
   }

   get messages() {
      return this._messageCollection.orderedItems
   }

   get name() {
      return this._name
   }

   get receivingAPIStatus() {
      return this._receivingAPIStatus
   }

   get loadingMessage() {
      return this._loadingMessage
   }

   get loadingMessageStatus(): LoadingMessageType | null {
      return this._loadingMessageStatus
   }

   getMessage(msgId: number): MessageModel {
      return this._messageCollection.getItem(msgId)
   }

   get lastMessage() {
      return this._messageCollection.orderedItems.at(-1)
   }

   get threadError() {
      return this._threadError || ''
   }

   setName(name: string) {
      this._name = name
   }

   addMessage(message: Message) {
      this._messageCollection.addItem(
         message.messageId,
         MessageModel.create(message)
      )
   }

   addRecentMessage(message: Message) {
      this._messageCollection.addItem(
         message.messageId,
         MessageModel.create(message),
         0
      )
   }

   deleteMessage(messageId: number) {
      this._messageCollection.deleteItem(messageId)
   }

   setReceivingAPIStatus(apiStatus: APIStatus) {
      this._receivingAPIStatus = apiStatus
   }

   setThreadError(threadError: ThreadError) {
      this._threadError = threadError
   }

   updateLoadingMessage(loadingMessage: string) {
      this._loadingMessage.setContent(loadingMessage)
   }

   updateLoadingMessageStatus(status: LoadingMessageType | null) {
      this._loadingMessageStatus = status
   }

   appendLoadingMessage(loadingMessage: string) {
      switch (loadingMessage) {
         case RESPONSE_LOADING_CONTENT.QUERIED_TO_AI: {
            this.updateLoadingMessageStatus('QUERIED_TO_AI')
            break
         }
         case RESPONSE_LOADING_CONTENT.EXTRACTING_INFO: {
            this.updateLoadingMessageStatus('EXTRACTING_INFO')
            break
         }

         case RESPONSE_LOADING_CONTENT.RETRIEVING_INFO: {
            this.updateLoadingMessageStatus('RETRIEVING_INFO')
            break
         }
         default: {
            this.updateLoadingMessageStatus('CONTENT_STREAMING')
            this.updateLoadingMessage(
               `${this._loadingMessage.content}${loadingMessage}`
            )
         }
      }
   }

   static create(data: Thread) {
      return new ThreadModel(data)
   }
}

export default ThreadModel
